import type { CSSProperties, FC, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import Card from 'app/modules/analytics/components/shared/Card';
import {
  CHART_PLACEHOLDER_CLASS_NAME,
  ChartPlaceholder,
  MAP_PLACEHOLDER_CLASS_NAME,
} from 'app/modules/analytics/components/shared/charts';

const SECTION_GRADIENT_STYLE: CSSProperties = {
  borderRadius: 'var(--3x, 12px)',
  background:
    'linear-gradient(273deg, rgba(121, 54, 204, 0.20) 2.37%, rgba(131, 54, 162, 0.20) 50.41%, rgba(202, 56, 86, 0.20) 97.55%)',
};

export type AnalyticsPageSkeletonVariant = 'performance' | 'content';

interface AnalyticsPageSkeletonProps {
  variant: AnalyticsPageSkeletonVariant;
}

function SectionShell({ children }: { children: ReactNode }) {
  return (
    <div className="tw-px-1 tw-pt-3" style={SECTION_GRADIENT_STYLE}>
      {children}
    </div>
  );
}

function SectionHeaderSkeleton() {
  return (
    <div
      className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-4"
      data-cy="analytics-page-skeleton_section-header"
    >
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2">
        <Skeleton className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-lg" />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-gap-2 tw-py-1">
          <Skeleton className="tw-h-5 tw-w-48 tw-max-w-full" />
          <Skeleton className="tw-h-4 tw-w-64 tw-max-w-full" />
        </div>
      </div>
      <Skeleton className="tw-h-7 tw-w-7 tw-shrink-0 tw-rounded-md" />
    </div>
  );
}

function MetricRowSkeleton() {
  return (
    <section className="tw-grid tw-grid-cols-1 tw-gap-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
      {Array.from({ length: 4 }, (_, index) => (
        <Card key={index} className="tw-h-full tw-w-full">
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
            <Skeleton className="tw-h-5 tw-w-24" />
            <div className="tw-flex tw-items-center tw-gap-2">
              <Skeleton containerClassName="tw-flex-1" className="tw-h-7" />
              <Skeleton className="tw-w-14 tw-h-6 tw-rounded-full" />
            </div>
          </div>
        </Card>
      ))}
    </section>
  );
}

function GraphCardSkeleton() {
  return (
    <div>
      <Card className="tw-gap-6">
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
          <Skeleton className="tw-h-4 tw-w-3/5 tw-max-w-[200px]" />
          <Skeleton className="tw-h-8 tw-w-2/5 tw-max-w-[140px]" />
          <ChartPlaceholder
            state="loading"
            className={CHART_PLACEHOLDER_CLASS_NAME}
          />
        </div>
      </Card>
    </div>
  );
}

function WideChartSkeleton({
  chartClassName,
  dataCy,
}: {
  chartClassName: string;
  dataCy: string;
}) {
  return (
    <div data-cy={dataCy}>
      <Card className="tw-gap-6">
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
          <Skeleton className="tw-h-4 tw-w-2/5 tw-max-w-[220px]" />
          <Skeleton className="tw-h-8 tw-w-1/4 tw-max-w-[120px]" />
          <ChartPlaceholder state="loading" className={chartClassName} />
        </div>
      </Card>
    </div>
  );
}

function ContentTableSkeleton() {
  return (
    <Card data-cy="analytics-page-skeleton_content-table">
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
        <Skeleton className="tw-h-6 tw-w-48 tw-max-w-full" />
        <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-3">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton
              key={index}
              className="tw-h-14 tw-mb-2 tw-w-full last:tw-mb-0"
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

const AnalyticsPageSkeleton: FC<AnalyticsPageSkeletonProps> = ({ variant }) => {
  return (
    <div data-cy="analytics-page-skeleton" data-variant={variant}>
      <SectionShell>
        <div className="tw-flex tw-flex-col tw-gap-1 tw-py-4">
          <MetricRowSkeleton />
          <MetricRowSkeleton />

          <section className="tw-grid tw-grid-cols-1 tw-gap-1 lg:tw-grid-cols-2 [&>*]:tw-min-w-0">
            <GraphCardSkeleton />
            <GraphCardSkeleton />
          </section>

          {variant === 'performance' ? (
            <WideChartSkeleton
              chartClassName={MAP_PLACEHOLDER_CLASS_NAME}
              dataCy="analytics-page-skeleton_map"
            />
          ) : (
            <ContentTableSkeleton />
          )}
        </div>
      </SectionShell>
    </div>
  );
};

export { SectionShell, SectionHeaderSkeleton, GraphCardSkeleton };

export default AnalyticsPageSkeleton;
